<div
  class="pt-2 md:pt-3 md:px-4 px-2 pb-8"
  data-testid="milestone-config-form-container"
>
  <div class="flex justify-content-between mb-3">
    <h1
      class="text-base md:text-lg flex align-items-center mb-0 line-height-1"
      data-testid="page-title"
    >
      {{ (editMode() ? 'MILESTONE.EDIT_MILESTONE_CONFIGURATION' : 'MILESTONE.CREATE_MILESTONE_CONFIGURATION') | translate }}
    </h1>
  </div>

  <div class="mt-1 mb-3" data-testid="breadcrumb-container">
    <app-breadcrumb />
  </div>

  <div class="grid grid-nogutter">
    <div class="col-12 xl:col-9">
  <!-- Basic Information -->
  <div
    class="surface-0 shadow-2 border-round p-4 mb-3"
    data-testid="basic-information-section"
  >
    <h2 class="text-base font-medium mb-3">
      {{ 'MILESTONE.BASIC_INFORMATION' | translate }}
    </h2>
    <form [formGroup]="milestoneForm">
      <div class="grid">
        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="name">
            {{ 'MILESTONE.MILESTONE_NAME' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <input
            pInputText
            id="name"
            formControlName="name"
            class="w-full"
            [placeholder]="'MILESTONE.MILESTONE_NAME' | translate"
            aria-label="milestone-name-input"
            data-testid="milestone-name-input"
          />
          @if (
            milestoneForm.get('name')?.touched &&
            milestoneForm.get('name')?.invalid
          ) {
            <small class="text-red-500 mt-1 block">
              {{ 'MILESTONE.MILESTONE_NAME_REQUIRED' | translate }}
            </small>
          }
        </div>

        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="tierName">
            {{ 'MILESTONE.TIER_NAME' | translate }}
          </label>
          <input
            pInputText
            id="tierName"
            formControlName="tierName"
            class="w-full"
            [placeholder]="'MILESTONE.TIER_NAME_PLACEHOLDER' | translate"
            aria-label="tier-name-input"
            data-testid="tier-name-input"
          />
        </div>
      </div>
    </form>
  </div>

  <!-- Milestone Configuration -->
  <div
    class="surface-0 shadow-2 border-round p-4 mb-3"
    data-testid="milestone-configuration-section"
  >
    <h2 class="text-base font-medium mb-3">
      {{ 'MILESTONE.MILESTONE_CONFIGURATION' | translate }}
    </h2>
    <form [formGroup]="milestoneForm">
      <div class="grid">
        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="milestoneType">
            {{ 'MILESTONE.MILESTONE_TYPE' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <p-select
            id="milestoneType"
            [options]="milestoneTypeOptions"
            formControlName="milestoneType"
            optionLabel="label"
            optionValue="value"
            [placeholder]="'MILESTONE.MILESTONE_TYPE' | translate"
            class="w-full"
            [disabled]="editMode()"
            aria-label="milestone-type-select"
            data-testid="milestone-type-select"
          />
        </div>

        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="comparisonType">
            {{ 'MILESTONE.COMPARISON_TYPE' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <p-select
            id="comparisonType"
            [options]="comparisonTypeOptions"
            formControlName="comparisonType"
            optionLabel="label"
            optionValue="value"
            [placeholder]="'MILESTONE.COMPARISON_TYPE' | translate"
            class="w-full"
            aria-label="comparison-type-select"
            data-testid="comparison-type-select"
          />
        </div>

        <!-- Conditional: Class Type Names (only for ClassCount) -->
        @if (milestoneForm.get('milestoneType')?.value === MilestoneType.ClassCount) {
          <div class="col-12">
            <label class="block mb-2 font-medium" for="classTypeNames">
              {{ 'MILESTONE.CLASS_TYPE_NAMES' | translate }}
            </label>
            <p-multiselect
              id="classTypeNames"
              [options]="classTypeOptions()"
              formControlName="classTypeNames"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'MILESTONE.CLASS_TYPE_NAMES_PLACEHOLDER' | translate"
              class="w-full"
              [loading]="loadingClassTypes()"
              aria-label="class-type-names-multiselect"
              data-testid="class-type-names-multiselect"
            />
            <small class="text-500 mt-1 block">
              {{ 'MILESTONE.CLASS_TYPE_NAMES_HINT' | translate }}
            </small>
          </div>
        }

        <!-- Conditional: Appointment Type Names (only for AppointmentCount) -->
        @if (milestoneForm.get('milestoneType')?.value === MilestoneType.AppointmentCount) {
          <div class="col-12">
            <label class="block mb-2 font-medium" for="appointmentTypeNames">
              {{ 'MILESTONE.APPOINTMENT_TYPE_NAMES' | translate }}
            </label>
            <p-multiselect
              id="appointmentTypeNames"
              [options]="appointmentTypeOptions()"
              formControlName="appointmentTypeNames"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'MILESTONE.APPOINTMENT_TYPE_NAMES_PLACEHOLDER' | translate"
              class="w-full"
              [loading]="loadingAppointmentTypes()"
              aria-label="appointment-type-names-multiselect"
              data-testid="appointment-type-names-multiselect"
            />
            <small class="text-500 mt-1 block">
              {{ 'MILESTONE.APPOINTMENT_TYPE_NAMES_HINT' | translate }}
            </small>
          </div>
        }

        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="targetValue">
            {{ 'MILESTONE.TARGET_VALUE' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <p-inputNumber
            inputId="targetValue"
            formControlName="targetValue"
            [min]="1"
            class="w-full"
            aria-label="target-value-input"
            data-testid="target-value-input"
          />
          @if (
            milestoneForm.get('targetValue')?.touched &&
            milestoneForm.get('targetValue')?.invalid
          ) {
            <small class="text-red-500 mt-1 block">
              {{ 'MILESTONE.TARGET_VALUE_REQUIRED' | translate }}
            </small>
          }
        </div>

        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="unit">
            {{ 'MILESTONE.UNIT' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <p-select
            id="unit"
            [options]="unitOptions()"
            formControlName="unit"
            optionLabel="label"
            optionValue="value"
            [placeholder]="'MILESTONE.UNIT' | translate"
            class="w-full"
            aria-label="unit-select"
            data-testid="unit-select"
          />
        </div>

        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="periodType">
            {{ 'MILESTONE.PERIOD_TYPE' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <p-select
            id="periodType"
            [options]="periodTypeOptions"
            formControlName="periodType"
            optionLabel="label"
            optionValue="value"
            [placeholder]="'MILESTONE.PERIOD_TYPE' | translate"
            class="w-full"
            aria-label="period-type-select"
            data-testid="period-type-select"
          />
        </div>

        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="startDate">
            {{ 'MILESTONE.START_DATE' | translate }}
          </label>
          <p-datepicker
            inputId="startDate"
            formControlName="startDate"
            [showIcon]="true"
            class="w-full"
            aria-label="start-date-picker"
            data-testid="start-date-picker"
          />
          <small class="text-500 mt-1 block">
            {{ 'MILESTONE.START_DATE_HINT' | translate }}
          </small>
        </div>

        <!-- Conditional: Week Start Day (only for Weekly period) -->
        @if (milestoneForm.get('periodType')?.value === MilestonePeriodType.Weekly) {
          <div class="col-12 md:col-6">
            <label class="block mb-2 font-medium" for="weekStartDay">
              {{ 'MILESTONE.WEEK_START_DAY' | translate }}
            </label>
            <p-select
              id="weekStartDay"
              [options]="weekStartDayOptions"
              formControlName="weekStartDay"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'MILESTONE.WEEK_START_DAY_PLACEHOLDER' | translate"
              class="w-full"
              aria-label="week-start-day-select"
              data-testid="week-start-day-select"
            />
          </div>
        }

        <!-- Conditional: Month Start Day (only for Monthly period) -->
        @if (milestoneForm.get('periodType')?.value === MilestonePeriodType.Monthly) {
          <div class="col-12 md:col-6">
            <label class="block mb-2 font-medium" for="monthStartDay">
              {{ 'MILESTONE.MONTH_START_DAY' | translate }}
            </label>
            <p-select
              id="monthStartDay"
              [options]="monthStartDayOptions"
              formControlName="monthStartDay"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'MILESTONE.MONTH_START_DAY_PLACEHOLDER' | translate"
              class="w-full"
              aria-label="month-start-day-select"
              data-testid="month-start-day-select"
            />
          </div>
        }

        <!-- Conditional: Custom Period Dates (only for Custom period) -->
        @if (milestoneForm.get('periodType')?.value === MilestonePeriodType.Custom) {
          <div class="col-12 md:col-6">
            <label class="block mb-2 font-medium" for="customPeriodStart">
              {{ 'MILESTONE.CUSTOM_PERIOD_START' | translate }}
            </label>
            <p-datepicker
              inputId="customPeriodStart"
              formControlName="customPeriodStart"
              [showIcon]="true"
              class="w-full"
              aria-label="custom-period-start-picker"
              data-testid="custom-period-start-picker"
            />
          </div>
          
          <div class="col-12 md:col-6">
            <label class="block mb-2 font-medium" for="customPeriodEnd">
              {{ 'MILESTONE.CUSTOM_PERIOD_END' | translate }}
            </label>
            <p-datepicker
              inputId="customPeriodEnd"
              formControlName="customPeriodEnd"
              [showIcon]="true"
              class="w-full"
              aria-label="custom-period-end-picker"
              data-testid="custom-period-end-picker"
            />
          </div>
        }
      </div>
    </form>
  </div>

  <!-- Reward Configuration -->
  <div
    class="surface-0 shadow-2 border-round p-4 mb-3"
    data-testid="reward-configuration-section"
  >
    <h2 class="text-base font-medium mb-3">
      {{ 'MILESTONE.REWARD_CONFIGURATION' | translate }}
    </h2>
    <form [formGroup]="milestoneForm">
      <div class="grid">
        <div class="col-12 md:col-6">
          <label class="block mb-2 font-medium" for="rewardType">
            {{ 'MILESTONE.REWARD_TYPE' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <p-select
            id="rewardType"
            [options]="rewardTypeOptions"
            formControlName="rewardType"
            optionLabel="label"
            optionValue="value"
            [placeholder]="'MILESTONE.REWARD_TYPE' | translate"
            class="w-full"
            aria-label="reward-type-select"
            data-testid="reward-type-select"
          />
        </div>
      </div>
    </form>
  </div>

  <!-- Action Buttons -->
  <div
    class="flex justify-content-end gap-2 mt-4 pt-4 border-top-1 surface-border"
    data-testid="action-buttons"
  >
    <button
      pButton
      type="button"
      [label]="'MILESTONE.CANCEL' | translate"
      class="p-button-text"
      (click)="onCancel()"
      aria-label="cancel-button"
      data-testid="cancel-button"
    ></button>
    <button
      pButton
      type="button"
      [label]="'MILESTONE.SAVE' | translate"
      (click)="onSave()"
      [loading]="loading()"
      aria-label="save-button"
      data-testid="save-button"
    ></button>
  </div>
    </div>

    @if (showAuditLogsFeature && configId()) {
      <div class="hidden xl:block xl:col-3 surface-0 text-sm text-600">
        <fitness-force-audit-trail-mini entityType="milestone_configurations"
          [entityId]="configId()!"
          [tenantId]="tenantId()"
          [tenantData]="[]"
          [maxEvents]="10"
          [showStats]="true"
          [showFilters]="true"
          [showPagination]="true"
          [showCloseButton]="false"
          height="100vh"
          title="Milestone Config Activity Log"
          data-testid="audit-trail-mini-component" />
      </div>
    }
  </div>
</div>
